import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { RootState } from "../../../store/root-reducer";
import { isScopeLoading } from "../../../store/scope-loading/helpers";
import ClaimViewModal from "./Modals/ClaimViewModal";
import TableGrid from "../../../components/TableGrid";
import { getClaims } from "../../../store/user/actions";
import { ClaimStatus, UserScope } from "../../../store/user/types";
import { convertToCRMDate, formatDate } from "../../../helper";

const statusbg: any = {
  [ClaimStatus.Draft]: "bg-gray-200 text-gray-600 border border-gray-600",
  [ClaimStatus.DraftReminder]:
    "bg-yellow-200 text-yellow-600 border border-yellow-600",
  [ClaimStatus.Adjudication]:
    "bg-blue-200 text-blue-600 border border-blue-600",
  [ClaimStatus.CustomerAction]:
    "bg-orange-200 text-orange-600 border border-orange-600",
  [ClaimStatus.CARResponse]:
    "bg-indigo-200 text-indigo-600 border border-indigo-600",
  [ClaimStatus.Approved]: "bg-green-200 text-green-600 border border-green-600",
  [ClaimStatus.Disposition]:
    "bg-purple-200 text-purple-600 border border-purple-600",
  [ClaimStatus.NonMonetary]: "bg-teal-200 text-teal-600 border border-teal-600",
  [ClaimStatus.StoreReferral]:
    "bg-amber-200 text-amber-600 border border-amber-600",
  [ClaimStatus.SoftClose]: "bg-cyan-200 text-cyan-600 border border-cyan-600",
  [ClaimStatus.Closed]:
    "bg-emerald-200 text-emerald-600 border border-emerald-600",
  [ClaimStatus.Denied]: "bg-red-200 text-red-600 border border-red-600",
  [ClaimStatus.ClaimedDeniedCustomerComm]:
    "bg-rose-200 text-rose-600 border border-rose-600",
  [ClaimStatus.Abandoned]:
    "bg-slate-200 text-slate-600 border border-slate-600",
};

function Claims({ data, info, loading, associated_dealers }: any) {
  const dispatch = useDispatch();
  const [claimDetailesModal, setClaimDetailesModal] = useState({
    show: false,
    claimId: "",
    claimNumber: "",
    status: "",
  });
  const searchDataRef = useRef(null);

  function close() {
    setClaimDetailesModal({
      show: false,
      claimId: "",
      claimNumber: "",
      status: "",
    });
  }

  useEffect(() => {
    if (data?.length === 0) {
      dispatch(getClaims({ page: 1 }));
    }
    return () => {};
  }, [data?.length, dispatch]);

  const loadData = (
    page: number = 1,
    per_page: number = 20,
    values?: any,
    clearSearch: boolean = false
  ) => {
    const searchPayload = values
      ? Object.keys(values).reduce((acc, key) => {
          if (values[key] && key === "claimDate") {
            acc[key] = convertToCRMDate(values[key]);
          } else if (key === "dealer_id" && values[key] === "all") {
            acc[key] = associated_dealers.map((d: any) => d.id).join(",");
          } else if (values[key]) {
            acc[key] = values[key];
          }
          return acc;
        }, {})
      : null;
    if (searchPayload) searchDataRef.current = searchPayload;
    if (clearSearch) searchDataRef.current = null;
    dispatch(
      getClaims({
        search: searchPayload || searchDataRef.current,
        page,
        per_page,
      })
    );
  };

  const tableHeaders: any = [
    { label: "Claim Number", key: "claimNumber" },
    {
      label: "Status",
      key: "status",
      align: "center",
      format: (val: any, row: any) => (
        <div className="flex items-center justify-center w-full">
          <div
            className={`inline-flex items-center justify-center  py-0.5 px-3 rounded-2xl text-[12px] ${statusbg[val]}`}
            role="presentation"
          >
            <span className="flex items-center">
              {row.denied && (
                <p className="mr-2 size-2.5 rounded-full bg-red-600 animate-pulse" />
              )}
              {val || "N/A"}
            </span>
          </div>
        </div>
      ),
    },
    { label: "Contract Number", key: "warrantyNumber" },
    { label: "Customer Name", key: "customerName" },
    {
      label: "Customer Address",
      key: "customerAddress",
    },
    {
      label: "Claim Date",
      key: "claimDate",
      format: (val: any) => formatDate(val, "N/A"),
    },
  ];

  if (associated_dealers.length > 1) {
    tableHeaders.splice(1, 0, {
      label: "Associated Dealer",
      key: "dealer_id",
      format: (val: any) =>
        associated_dealers.find((d: any) => d.id === val)?.alias || val,
    });
  }

  return (
    <div className="container">
      <TableGrid
        data={data}
        loading={loading}
        searchComponentProps={{
          onSearchSubmit: (values, perPage) => loadData(1, perPage, values),
          onClearFilter: (perPage) => loadData(1, perPage, null, true),
          searchFields: [
            ...(associated_dealers.length > 1
              ? ([
                  {
                    label: "Associated Dealer",
                    name: "dealer_id",
                    type: "select",
                    defaultValue: { label: "All", value: "all" },
                    options: [
                      { label: "All", value: "all" },
                      ...associated_dealers.map((d: any) => ({
                        label: d.alias,
                        value: d.id,
                      })),
                    ],
                  },
                ] as any)
              : []),
            {
              label: "Contract Number",
              name: "warrantyNumber",
              type: "text",
            },
            {
              label: "Customer Name",
              name: "customerName",
              type: "text",
            },
            {
              label: "Claim Number",
              name: "claimNumber",
              type: "text",
            },
            {
              label: "Claim Date",
              name: "claimDate",
              type: "date",
            },
            {
              label: "Invoice Number",
              name: "invoice_number",
              type: "text",
            },
            {
              label: "Customer Phone",
              name: "customer_phone",
              type: "text",
            },
            {
              label: "Customer Email",
              name: "customer_email",
              type: "email",
            },
          ],
        }}
        loadMoreData={(per_page) => {
          const nextPage = info?.page + 1;
          if (info?.more_records) {
            dispatch(getClaims({ page: nextPage, per_page }));
          }
        }}
        onPerPageChange={(per_page) => {
          dispatch(getClaims({ page: 1, per_page }));
        }}
        headers={tableHeaders}
        handleViewButtonClick={(data) =>
          setClaimDetailesModal({
            show: true,
            claimId: data.id,
            claimNumber: data.claimNumber,
            status: data.status,
          })
        }
      />
      <ClaimViewModal
        claimId={claimDetailesModal.claimId}
        claimNumber={claimDetailesModal.claimNumber}
        isOpen={claimDetailesModal.show}
        close={close}
        status={claimDetailesModal.status}
      />
    </div>
  );
}

const mapStateProps = (state: RootState) => {
  return {
    data: state.user.claims?.data || [],
    info: state.user.claims?.info || {},
    loading: isScopeLoading(state.scopeLoading, `${UserScope}-claims`),
    associated_dealers: state.user.selectedOrg.associated_dealers || [],
  };
};

export default connect(mapStateProps)(Claims);
